import React, { useState, useEffect, useCallback, useRef } from 'react';
import api, { asArray } from '../api';
import { distanceBetween } from '../lib/route';
import { formatSpan } from '../lib/datetime';
import {
  SearchIcon, CloseIcon, LocateIcon, PlusIcon, MapIcon, CarIcon,
} from './icons';

const FILTERS = [
  { id: 'all', label: 'Alla' },
  { id: 'parking', label: 'Parkering' },
  { id: 'fuel', label: 'Bensinstationer' },
  { id: 'repair', label: 'Verkstäder' },
];

const VEHICLES = [
  { id: 'epa', label: 'EPA', speed: 30 },
  { id: 'moped', label: 'Moped', speed: 45 },
];

const VEHICLE_KEY = 'vehicle';
// Adressuppslagningen gar via en proxy som slapper igenom ett anrop i
// sekunden. En kortare fordrojning bygger bara ko.
const SEARCH_DEBOUNCE_MS = 450;

const formatDistance = (metres) => (
  metres < 1000 ? `${Math.round(metres)} m` : `${(metres / 1000).toFixed(1)} km`
);

/**
 * Kartvyn: sökfält, filter, kartkontroller och platskortet.
 *
 * Lager ovanpå kartan i stället för en egen sida — kartan ska synas hela
 * tiden, och det man trycker på ska kännas som en del av den.
 */
export default function MapScreen({
  map, userPosition, meets, filter, onFilter,
  selectedPlace, onSelectPlace, onNavigate, busy, onLocationGranted,
}) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [vehicle, setVehicle] = useState(() => localStorage.getItem(VEHICLE_KEY) || 'epa');
  const [error, setError] = useState('');
  // Lases vid anropet i stallet for att starta om sokningen nar de andras.
  const meetsRef = useRef(meets);
  const positionRef = useRef(userPosition);
  const searchId = useRef(0);

  useEffect(() => { meetsRef.current = meets; }, [meets]);
  useEffect(() => { positionRef.current = userPosition; }, [userPosition]);

  const chooseVehicle = (id) => {
    setVehicle(id);
    try { localStorage.setItem(VEHICLE_KEY, id); } catch { /* privat läge */ }
  };

  /**
   * Sökningen tittar både i adressregistret och bland träffarna, eftersom
   * fältet lovar båda.
   *
   * Bara texten får starta om den. Positionen och träfflistan läses ur
   * refs: userPosition byts vid varje GPS-uppdatering, och låg den i
   * beroendelistan avbröts den pågående sökningen flera gånger i sekunden
   * och hann aldrig bli klar — fältet såg ut att söka i all oändlighet.
   *
   * Träffarna matchas lokalt och visas direkt; adresserna fyller på när de
   * kommer. Ett löpnummer gör att ett sent svar inte kan skriva över ett
   * nyare.
   */
  useEffect(() => {
    const text = query.trim();
    if (text.length < 2) {
      setResults([]);
      setSearching(false);
      return undefined;
    }

    const lowered = text.toLowerCase();
    const localMatches = meetsRef.current
      .filter(m => (m.title || '').toLowerCase().includes(lowered))
      .slice(0, 3)
      .map(m => ({
        key: `meet-${m.id}`,
        kind: 'meet',
        name: m.title,
        detail: formatSpan(m.event_date, m.end_date),
        lat: Number(m.lat),
        lng: Number(m.lng),
        address: m.address,
      }));

    // Egna träffar kräver inget nätverk och ska synas med en gång.
    setResults(localMatches);

    if (text.length < 3) {
      setSearching(false);
      return undefined;
    }

    const attempt = ++searchId.current;
    setSearching(true);

    const timer = setTimeout(async () => {
      const params = { q: text, limit: 5 };
      const here = positionRef.current;
      if (here) {
        params.lat = here[0];
        params.lng = here[1];
      }

      try {
        const res = await api.get('/geocode/search', { params });
        if (attempt !== searchId.current) return;

        const addresses = asArray(res.data).map(hit => ({
          key: `place-${hit.place_id}`,
          kind: 'address',
          name: hit.display_name.split(',')[0],
          detail: hit.display_name.split(',').slice(1, 3).join(',').trim(),
          lat: hit.lat,
          lng: hit.lon,
          address: hit.display_name,
        }));
        setResults([...localMatches, ...addresses]);
      } catch {
        // Adressökningen kan vara nere; träffarna fungerar ändå.
      } finally {
        if (attempt === searchId.current) setSearching(false);
      }
    }, SEARCH_DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [query]);

  const pick = (result) => {
    setQuery('');
    setResults([]);
    onSelectPlace({
      id: result.key,
      category: result.kind,
      categoryLabel: result.kind === 'meet' ? 'Träff' : 'Plats',
      name: result.name,
      address: result.address || result.detail,
      details: result.kind === 'meet' ? result.detail : '',
      lat: result.lat,
      lng: result.lng,
    });
    if (map) map.setView([result.lat, result.lng], 16);
  };

  /**
   * Centrerar pa anvandaren. Saknas positionen bes webblasaren om
   * tillstand harifran — knappen ar det narmaste man kommer en
   * "visa mig var jag ar", och da ska den ocksa fraga.
   */
  const recentre = useCallback(() => {
    if (userPosition) {
      if (map) map.setView(userPosition, 16);
      return;
    }

    if (!navigator.geolocation) {
      setError('Enheten stödjer inte platsbestämning.');
      setTimeout(() => setError(''), 4000);
      return;
    }

    // Rensar markeringen om att fragan redan stallts pa enheten.
    try { localStorage.removeItem('locationPromptDismissed'); } catch { /* privat läge */ }

    setError('Söker din position…');
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setError('');
        if (map) map.setView([pos.coords.latitude, pos.coords.longitude], 16);
        if (onLocationGranted) onLocationGranted();
      },
      () => {
        setError('Vi fick inte tillgång till din plats. Se Profil → inställningar.');
        setTimeout(() => setError(''), 6000);
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  }, [map, userPosition, onLocationGranted]);

  const distanceToSelected = selectedPlace && userPosition
    ? distanceBetween(userPosition, [selectedPlace.lat, selectedPlace.lng])
    : null;

  const speed = VEHICLES.find(v => v.id === vehicle).speed;
  // Fågelvägen underskattar färdvägen; en fjärdedels påslag ligger närmare.
  const roughMinutes = distanceToSelected
    ? Math.max(1, Math.round(((distanceToSelected * 1.25) / 1000 / speed) * 60))
    : null;

  return (
    <>
      <div className="map-ui">
        <div className="map-search-wrap">
          <div className="map-search">
            <span className="map-search__icon"><SearchIcon size={18} /></span>
            <input
              value={query}
              onChange={e => setQuery(e.target.value)}
              placeholder="Sök efter platser, adresser eller träffar…"
              aria-label="Sök"
              autoCapitalize="none"
              autoCorrect="off"
            />
            {searching && <span className="map-search__spinner" aria-label="Söker" />}
            {query && !searching && (
              <button className="map-search__clear" onClick={() => setQuery('')} aria-label="Rensa">
                <CloseIcon size={15} />
              </button>
            )}
          </div>

          {/* Ligger ovanpa i stallet for att knuffa ner filterraden — annars
              hoppar hela ytan varje gang ett resultat kommer eller gar. */}
          {(results.length > 0 || searching) && (
            <div className="map-results">
              {results.map(result => (
                <button key={result.key} className="map-result" onClick={() => pick(result)}>
                  <span className="map-result__icon">
                    {result.kind === 'meet' ? <CarIcon size={17} /> : <MapIcon size={17} />}
                  </span>
                  <span className="map-result__text">
                    <span className="map-result__name">{result.name}</span>
                    {result.detail && <span className="map-result__detail">{result.detail}</span>}
                  </span>
                </button>
              ))}
              {searching && <p className="map-results__empty">Söker adresser…</p>}
            </div>
          )}
        </div>

        <div className="map-filters">
          {FILTERS.map(item => (
            <button
              key={item.id}
              className={filter === item.id ? 'map-filter is-active' : 'map-filter'}
              onClick={() => onFilter(item.id)}
            >
              {item.label}
            </button>
          ))}
        </div>
      </div>

      <div className="map-controls">
        <button className="map-control" onClick={() => map && map.zoomIn()} aria-label="Zooma in">
          <PlusIcon size={22} />
        </button>
        <button className="map-control" onClick={() => map && map.zoomOut()} aria-label="Zooma ut">
          <span className="map-control__minus" aria-hidden="true" />
        </button>
        <button className="map-control map-control--locate" onClick={recentre} aria-label="Min position">
          <LocateIcon size={21} />
        </button>
      </div>

      {error && <div className="map-hint map-hint--muted">{error}</div>}
      {busy && <div className="map-hint map-hint--muted">Hämtar platser…</div>}

      {selectedPlace && (
        <div className="place-sheet">
          <div className="place-sheet__grip" aria-hidden="true" />

          <button className="place-sheet__close" onClick={() => onSelectPlace(null)} aria-label="Stäng">
            <CloseIcon size={17} />
          </button>

          <p className="place-sheet__kind">{selectedPlace.categoryLabel}</p>
          <h2 className="place-sheet__name">{selectedPlace.name}</h2>

          {selectedPlace.address && (
            <p className="place-sheet__row">
              <MapIcon size={15} />
              {selectedPlace.address}
            </p>
          )}

          {distanceToSelected !== null && (
            <p className="place-sheet__row place-sheet__row--strong">
              {formatDistance(distanceToSelected)} · ca {roughMinutes} min
            </p>
          )}

          {selectedPlace.details && (
            <p className="place-sheet__row place-sheet__row--muted">{selectedPlace.details}</p>
          )}

          <div className="place-sheet__vehicle">
            {VEHICLES.map(v => (
              <button
                key={v.id}
                className={vehicle === v.id ? 'is-active' : ''}
                onClick={() => chooseVehicle(v.id)}
              >
                {v.label} {v.speed}
              </button>
            ))}
          </div>

          <button
            className="btn btn--primary btn--block btn--lg"
            onClick={() => onNavigate(selectedPlace, speed)}
          >
            Navigera →
          </button>
        </div>
      )}
    </>
  );
}
